import { JsonFormsLayout } from './JsonFormLayout';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rankWith, uiTypeIs } from '@jsonforms/core';
import type { RankedTester, RendererProps, Layout } from '@jsonforms/core';
import type { Labeled } from '@jsonforms/core/src/models/uischema';
import { withJsonFormsLayoutProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import React, { useState } from 'react';
import { gridClasses } from 'src/components/JsonForm/layouts/GridLayout';
import { RenderChildren } from 'src/components/JsonForm/RenderChildren';
import { Button } from 'src/components/ui';

export interface GridCollapsibleLayout extends Layout, Labeled {
    type: 'GridCollapsibleLayout';
}

export const GridCollapsibleLayoutTester: RankedTester = rankWith(10, uiTypeIs('GridCollapsibleLayout'));

export const GridCollapsibleLayoutStableRenderer = (props: RendererProps) => {
    const { data: _data, ...otherProps } = props;
    // We don't hand over data to the layout renderer to avoid rerendering it with every data change
    return <GridCollapsibleLayoutRendererMemoizedComponent {...otherProps} />;
};

const GridCollapsibleLayoutRendererComponent = ({ schema, uischema, enabled, visible, path }: RendererProps) => {
    const layout = uischema as GridCollapsibleLayout;

    const visibleElements = layout.elements.filter((element) => element?.rule?.effect !== 'HIDE');

    const [open, setOpen] = useState(false);

    return visible && visibleElements.length > 0 ? (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-1.5">
                <div className="h-px w-full bg-gray-500" />
                <div>
                    <Button
                        onClick={() => setOpen(!open)}
                        variant="ghost"
                        size="xs"
                        className="h-6 px-1.5 text-[8px] text-gray-400 uppercase"
                    >
                        <span>{layout.label}</span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={cn('ml-1.5 h-2 w-2 shrink-0 transition-transform', !open && 'rotate-90')}
                        />
                    </Button>
                </div>
            </div>
            {open && layout.elements.length > 0 && (
                <JsonFormsLayout
                    className={gridClasses}
                    visible={visible}
                    enabled={enabled}
                    path={path}
                    uischema={uischema}
                    schema={schema}
                >
                    <RenderChildren
                        layout={layout}
                        schema={schema}
                        path={path}
                        enabled={enabled}
                        className="[&:empty]:hidden"
                    />
                </JsonFormsLayout>
            )}
        </div>
    ) : null;
};

const GridCollapsibleLayoutRendererMemoizedComponent = React.memo(GridCollapsibleLayoutRendererComponent);

export const GridCollapsibleLayoutRenderer = withJsonFormsLayoutProps(GridCollapsibleLayoutStableRenderer, false);
